import React from 'react';
import Dropzone from 'react-dropzone';
import { Field } from 'redux-form';
import styled from 'styled-components';
import chroma from 'chroma-js';

import { FormControl } from 'components/Shared/Forms';
import { LinkButton } from 'components/Shared/Buttons';
import { Icon, GLYPHS } from 'components/Shared/Icon';
import { COLORS, SCAFFOLDING, TRANSITION, TYPOGRAPHY } from 'theme';
import notify from 'utils/notify';
import { postFile } from 'utils/http';

const StyledDropzone = styled(Dropzone)`
    padding: ${SCAFFOLDING.GUTTER};
    border: 2px dotted ${chroma(TYPOGRAPHY.COLOR_REDUCED).alpha(0.4).css()};
    cursor: pointer;
    color: ${TYPOGRAPHY.COLOR_REDUCED};
    text-align: center;
    transition: all ${TRANSITION.BEZIER} ${TRANSITION.LENGTH};

    &:hover {
        border-color: ${chroma(TYPOGRAPHY.COLOR_REDUCED).alpha(0.75).css()};
    }

    &.active {
        border-color: ${chroma(COLORS.SUCCESS).alpha(0.4).css()};
        color: ${COLORS.SUCCESS};
    }

    &.reject {
        border-color: ${chroma(COLORS.DANGER).alpha(0.4).css()};
        color: ${COLORS.DANGER};
    }
`;

const StyledDropzoneIcon = styled(Icon)`
    margin-bottom: ${SCAFFOLDING.GUTTER_FACTOR(0.125)};
    font-size: 3em;
`;

const StyledUploadedFile = styled.div`
    position: relative;
    padding: ${SCAFFOLDING.GUTTER};
    border: 2px dotted ${chroma(TYPOGRAPHY.COLOR_REDUCED).alpha(0.4).css()};
    overflow: auto;
    text-align: center;
`;

const StyledUploadedImageWrapper = styled.div`
    display: inline-block;
    position: relative;
`;

const StyledUploadedImage = styled.img`
    display: inline-block;
    border: 1px solid #fff;
`;

const StyledUploadedRemove = styled(LinkButton)`
    margin-top: 0.125em;
`;

const StyledAnchor = styled.div`
    position: absolute;
    width: ${SCAFFOLDING.GUTTER_FACTOR(0.4)};
    height: ${SCAFFOLDING.GUTTER_FACTOR(0.4)};
    transform: translate(-50%, -50%);
`;

const StyledAnchorInner = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    transform: rotate(45deg);
`;

const StyledAnchorBg = styled(StyledAnchorInner)`
    &:before,
    &:after {
        content: '';
        display: block;
        position: absolute;
        background-color: #333;
    }

    &:before {
        top: 1px;
        bottom: 1px;
        left: 50%;
        width: 2px;
        margin-left: -1px;
    }

    &:after {
        top: 50%;
        right: 1px;
        left: 1px;
        height: 2px;
        margin-top: -1px;
    }
`;

const StyledAnchorBorder = styled(StyledAnchorInner)`
    &:before,
    &:after {
        content: '';
        display: block;
        position: absolute;
        background-color: #fff;
    }

    &:before {
        top: 0;
        bottom: 0;
        left: 50%;
        width: 4px;
        margin-left: -2px;
    }

    &:after {
        top: 50%;
        right: 0;
        left: 0;
        height: 4px;
        margin-top: -2px;
    }
`;

class IconUploadField extends React.Component {
    handleAccepted = (filesToUpload) => {
        const {
            url,
            input: { onChange },
        } = this.props;
        if (url && filesToUpload && filesToUpload.length > 0) {
            postFile(url, filesToUpload[0])
                .then(onChange)
                .catch(() => {
                    notify.error('Invalid icon upload. Please try a different image');
                });
        }
    };

    handleRejected = () => {
        notify.error('Please select a valid image file no larger than 3 MB.');
    };

    handleRemoveFile = (e) => {
        e.preventDefault();
        this.props.input.onChange(null);
    };

    renderInnerDropzone = ({ isDragActive, isDragReject }) => {
        let message;
        let glyph;
        if (isDragActive) {
            glyph = GLYPHS.CHECK;
            message = 'That file looks good, drop it here to upload!';
        } else if (isDragReject) {
            glyph = GLYPHS.CLOSE;
            message = 'Sorry, that file type is not allowed.';
        } else {
            glyph = GLYPHS.PUBLISH;
            message = 'Click to select an icon to upload, or drag and drop your icon here.';
        }
        return (
            <div>
                <StyledDropzoneIcon glyph={glyph} />
                <div>{message}</div>
            </div>
        );
    };

    renderUploadedFile(value, size, anchor) {
        return (
            <div>
                <StyledUploadedFile>
                    <StyledUploadedImageWrapper>
                        <StyledUploadedImage
                            src={value.url}
                            alt="Marker Icon"
                            width={size.width}
                            height={size.height}
                        />
                        <StyledAnchor style={{ top: anchor.y, left: anchor.x }}>
                            <StyledAnchorBorder />
                            <StyledAnchorBg />
                        </StyledAnchor>
                    </StyledUploadedImageWrapper>
                </StyledUploadedFile>
                <StyledUploadedRemove onClick={this.handleRemoveFile}>Remove Image</StyledUploadedRemove>
            </div>
        );
    }

    render() {
        const {
            input: { name, value },
            meta: { touched, error },
            size,
            anchor,
        } = this.props;
        const hasValue = value && value.url;
        const hasError = touched && error;
        return (
            <FormControl width="100%" {...this.props} className="reject">
                {!hasValue && (
                    <StyledDropzone
                        className={hasError ? 'reject' : null}
                        name={name}
                        onDropAccepted={this.handleAccepted}
                        onDropRejected={this.handleRejected}
                        multiple={false}
                        maxSize={3 * 1024 * 1024}
                        accept="image/jpeg, image/jpg, image/png, image/gif, image/svg+xml"
                        activeClassName="active"
                        rejectClassName="reject"
                    >
                        {this.renderInnerDropzone}
                    </StyledDropzone>
                )}
                {hasValue && this.renderUploadedFile(value, size, anchor)}
            </FormControl>
        );
    }
}

const IconUpload = (props) => <Field component={IconUploadField} {...props} />;

export default IconUpload;
